import type { CardBackground, CardLook } from '@vp/shared-types';

/**
 * How a look (module API 4.10) is drawn on a profile card or on the tray: the frame (radius, border,
 * shadow, blur of what is under it) on the card itself, the fill (background with its opacity) and the
 * motion on a backdrop layer under the content (`CardBackdrop.vue`).
 */

export function backgroundCss(bg: CardBackground): Record<string, string> {
  if (bg.kind === 'color') return { background: bg.color };
  if (bg.kind === 'gradient') {
    const stops = bg.stops.map((s) => `${s.color} ${Math.round(s.at * 100)}%`).join(', ');
    return { backgroundImage: bg.type === 'radial' ? `radial-gradient(circle at 50% 50%, ${stops})` : `linear-gradient(${bg.angle ?? 135}deg, ${stops})` };
  }
  return {
    backgroundImage: `url("${bg.src}")`,
    backgroundSize: bg.fit === 'tile' ? 'auto' : bg.fit,
    backgroundRepeat: bg.fit === 'tile' ? 'repeat' : 'no-repeat',
    backgroundPosition: 'center',
  };
}

/** Whether the look paints a backdrop of its own (then the card's own background steps aside). */
export function hasFill(look: CardLook): boolean {
  return !!look.background || look.opacity !== undefined || (!!look.motion && look.motion !== 'none');
}

/** The frame of a card or the tray: on the element itself. */
export function frameCss(look: CardLook): Record<string, string> {
  const out: Record<string, string> = {};
  if (look.radius !== undefined) out.borderRadius = look.radius + 'px';
  if (look.border === null) out.border = 'none';
  else if (look.border) out.border = `${look.border.width}px solid ${look.border.color}`;
  if (look.shadow === 'none') out.boxShadow = 'none';
  else if (look.shadow === 'strong') out.boxShadow = 'var(--shadow-strong)';
  else if (look.shadow === 'soft') out.boxShadow = 'var(--shadow-soft)';
  if (look.blur) {
    out.backdropFilter = `blur(${look.blur}px)`;
    out.WebkitBackdropFilter = `blur(${look.blur}px)`;
  }
  if (hasFill(look)) out.background = 'transparent';
  return out;
}

/** The backdrop under the content: the background (or the card's usual one) at the look's opacity. */
export function fillCss(look: CardLook, base: string): Record<string, string> {
  return { ...(look.background ? backgroundCss(look.background) : { background: base }), opacity: String(look.opacity ?? 1) };
}
